// Reports ▸ Order timeline: the story of one PO (or one product on it) from creation to delivery, with the original
// plan (a frozen baseline), the latest plan and what actually happened. Shared by the server and the screens.

export type MilestoneKey = "po_placed" | "techpack_approved" | "lab_dips" | "fit_sample" | "pps" | "fabric_in_house" | "fabric_test" | "production_start"
  | "inline_inspection" | "final_inspection" | "ex_factory" | "etd" | "eta" | "delivered";
export const MILESTONES: Array<[MilestoneKey, string]> = [
  ["po_placed", "PO placed"], ["techpack_approved", "Tech pack approved"], ["lab_dips", "Lab dips approved"], ["fit_sample", "Fit sample approved"],
  ["pps", "PPS / gold seal approved"], ["fabric_in_house", "Bulk fabric in-house"], ["fabric_test", "Bulk fabric test approved"], ["production_start", "Production start"],
  ["inline_inspection", "Inline inspection"], ["final_inspection", "Final inspection"], ["ex_factory", "Ex-factory"], ["etd", "ETD"], ["eta", "ETA"], ["delivered", "Delivered"],
];
export const MILESTONE_LABEL = Object.fromEntries(MILESTONES) as Record<MilestoneKey, string>;
export const isMilestone = (k: unknown): k is MilestoneKey => MILESTONES.some(([m]) => m === k);

export type ShipMode = "" | "Sea" | "Air" | "Road" | "Courier";
export const SHIP_MODES: ShipMode[] = ["Sea", "Air", "Road", "Courier"];
const TRANSIT: Record<string, number> = { Sea: 30, Air: 5, Road: 7, Courier: 4, "": 30 };

export const addDays = (d: string, n: number) => { const t = new Date(`${d}T00:00:00Z`); t.setUTCDate(t.getUTCDate() + n); return t.toISOString().slice(0, 10); };
export const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);

/** A time & action calendar (industry lead times) from the PO's dates, when no date has been set for a milestone. */
export function defaultPlan(p: { orderDate: string | null; exFactory: string | null; etd: string | null; eta: string | null; shipMode: ShipMode }): Record<MilestoneKey, string | null> {
  const order = p.orderDate;
  const exf = p.exFactory || (p.etd ? addDays(p.etd, -7) : null);
  const etd = p.etd || (exf ? addDays(exf, 7) : null);
  const eta = p.eta || (etd ? addDays(etd, TRANSIT[p.shipMode] ?? 30) : null);
  const back = (n: number) => (exf ? addDays(exf, -n) : null);
  return {
    po_placed: order, techpack_approved: order ? addDays(order, 7) : null, lab_dips: order ? addDays(order, 14) : null, fit_sample: order ? addDays(order, 21) : null,
    pps: back(35), fabric_in_house: back(30), fabric_test: back(28), production_start: back(25), inline_inspection: back(12), final_inspection: back(3),
    ex_factory: exf, etd, eta, delivered: eta ? addDays(eta, 5) : null,
  };
}

export interface Baseline {
  seq: number; kind: "original" | "rebaseline" | "reconstructed"; reason: string; by: string; at: string;
  plan: Record<MilestoneKey, string | null>; shipMode: ShipMode;
  lines: Array<{ key: string; code: string; colour: string; qtyBySize: Record<string, number>; quantity: number; unitPrice: number; sizeSet?: string[] }>;
  orderValue: number;
}
export const lineKey = (code: string, colour: string) => `${code}|${(colour || "").toLowerCase()}`;

// ---- Delay reasons ----------------------------------------------------------------------------------------------------
export const DEFAULT_DELAY_REASONS = [
  "Fabric/trims late", "Lab dip rejected", "Sample rejected", "Tech pack/approval late", "Buyer change", "Factory capacity",
  "Quality issue/re-work", "Testing/certification", "Shipping/port/vessel", "Payment", "Other",
];
export type Responsible = "supplier" | "buyer" | "external";
export const RESPONSIBLE: Array<[Responsible, string]> = [["supplier", "Supplier"], ["buyer", "Us (buyer side)"], ["external", "External"]];
export const RESPONSIBLE_LABEL = Object.fromEntries(RESPONSIBLE) as Record<Responsible, string>;
export interface TimelineSettings { reasons: string[]; amberDays: number }
export function normalizeTimelineSettings(v: any): TimelineSettings {
  const list = Array.isArray(v?.reasons) ? [...new Set(v.reasons.map((x: unknown) => String(x ?? "").trim().slice(0, 80)).filter(Boolean))] as string[] : DEFAULT_DELAY_REASONS;
  if (!list.includes("Other")) list.push("Other");
  const amber = Math.floor(Number(v?.amberDays));
  return { reasons: list.slice(0, 40), amberDays: Number.isFinite(amber) && amber >= 1 && amber <= 60 ? amber : 7 };
}
/** Green = on time or early; amber = up to the threshold late; red = later than that; grey = not yet happened. */
export function varianceTone(variance: number | null, amberDays: number): "green" | "amber" | "red" | "grey" {
  if (variance === null) return "grey";
  return variance <= 0 ? "green" : variance <= amberDays ? "amber" : "red";
}

/** Plain-English differences between two versions of a PO's lines (quantities per size, prices, lines added/removed). */
export function lineChanges(
  before: Array<{ code: string; colour: string; qtyBySize: Record<string, number>; quantity: number; unitPrice: number; sizeSet?: string[] }>,
  after: Array<{ code: string; colour: string; qtyBySize: Record<string, number>; quantity: number; unitPrice: number; sizeSet?: string[] }>,
  prices: boolean,
): string[] {
  const out: string[] = [];
  const a = new Map(before.map((l) => [lineKey(l.code, l.colour), l])), b = new Map(after.map((l) => [lineKey(l.code, l.colour), l]));
  const name = (l: { code: string; colour: string }) => `${l.code}${l.colour ? ` ${l.colour}` : ""}`;
  for (const [k, l] of b) {
    const o = a.get(k);
    if (!o) { out.push(`Added ${name(l)}`); continue; }
    const sizes = [...new Set([...(o.sizeSet || []), ...(l.sizeSet || []), ...Object.keys(o.qtyBySize || {}), ...Object.keys(l.qtyBySize || {})])];
    if (sizes.length && Object.keys({ ...o.qtyBySize, ...l.qtyBySize }).length) {
      for (const s of sizes) {
        const x = Number(o.qtyBySize?.[s] || 0), y = Number(l.qtyBySize?.[s] || 0);
        if (x !== y) out.push(`${name(l)}: qty size ${s} ${x} → ${y}`);
      }
    } else if (o.quantity !== l.quantity) out.push(`${name(l)}: qty ${o.quantity} → ${l.quantity}`);
    if (prices && Number(o.unitPrice) !== Number(l.unitPrice)) out.push(`${name(l)}: price ${o.unitPrice} → ${l.unitPrice}`);
  }
  for (const [k, o] of a) if (!b.has(k)) out.push(`Removed ${name(o)}`);
  return out;
}

export type TimelineStage = "order" | "techpack" | "documents" | "sampling" | "labels" | "production" | "shipping" | "finance" | "other";
export const TIMELINE_STAGES: Array<[TimelineStage, string]> = [
  ["order", "Order placed & revisions"], ["techpack", "Tech pack & approvals"], ["documents", "Documents sent to supplier"], ["sampling", "Sampling"],
  ["labels", "Labels & packaging"], ["production", "Production & QC"], ["shipping", "Shipping"], ["finance", "Payments & invoices (Admins only)"], ["other", "Other events"],
];
export const STAGE_LABEL_TL = Object.fromEntries(TIMELINE_STAGES) as Record<TimelineStage, string>;
export interface TimelineEvent {
  stage: TimelineStage; at: string; who: string; text: string; lineId: string | null; key: boolean;
  link?: { href: string; label: string }; included?: string[]; alert?: boolean; manual?: { id: string; by: string; canEdit: boolean; plannedDate: string | null; fileId: string | null; fileName?: string };
}
